import React, { useState, useEffect, useMemo } from "react";

import { Tooltip, Button, Chip, Grid } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";

import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";

import ReportNoteTemplateModal from "./ReportNoteTemplateModal";
import ConfirmModal from "components/common/modals/confirmModal/ConfirmModal";
import {
  deleteReportNoteTemplate,
  getReportNoteTemplates,
  getReportNoteTemplateSelectors,
} from "app/services/report_templates/reportNoteTemplateService";
import CustomTable from "components/common/table/CustomTable";
import { MRT_GlobalFilterTextField as GlobalFilterTextField } from "material-react-table";
import { showErrorToast, showSuccessToast } from "utils/toast";
import { DEFAULT_SMALL_PAGE_SIZE_OPTIONS } from "static/pagination";
import { booleanFilter, textFilter } from "utils/table";

const ReportNoteTemplatesView = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [reportNoteTemplates, setReportNoteTemplates] = useState([]);
  const [reportTemplateSelectors, setReportTemplateSelectors] = useState([]);

  const fetchReportNoteTemplates = async () => {
    try {
      setIsLoading(true);

      const data = await getReportNoteTemplates();
      setReportNoteTemplates(data);
    } catch (error) {
      showErrorToast(error?.message ?? error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchReportTemplateSelectors = async () => {
    try {
      setIsLoading(true);

      const data = await getReportNoteTemplateSelectors();
      setReportTemplateSelectors(data || []);
    } catch (error) {
      showErrorToast(error?.message ?? error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReportNoteTemplates();
    fetchReportTemplateSelectors();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const deleteNoteTemplate = async () => {
    try {
      setIsDeleting(true);
      await deleteReportNoteTemplate(selectedRow?._id);
      showSuccessToast("Report Note Template deleted successfully", false);
    } catch (error) {
      showErrorToast(error?.message ?? error);
    } finally {
      setIsDeleting(false);
      setSelectedRow(null);
      setShowConfirmModal(false);
    }
  };

  const onConfirm = async () => {
    await deleteNoteTemplate();
    await fetchReportNoteTemplates();
  };

  const onCloseModal = () => {
    setShowEditModal(false);
    setIsEditMode(false);
    setSelectedRow(null);
  };

  const onConfirmModalClose = () => {
    setShowConfirmModal(false);
    setSelectedRow(null);
  };

  const columns = useMemo(
    () => [
      {
        header: "Device Group",
        accessorKey: "device_group_name",
        filterVariant: "text",
        filterFn: textFilter,
      },
      {
        header: "Billing Code",
        accessorKey: "billingCode",
        size: 180,
        filterFn: textFilter,
      },
      {
        header: "Account",
        accessorKey: "account",
        filterVariant: "text",
        filterFn: textFilter,
      },
      {
        header: "Template Sections",
        accessorKey: "sections",
        size: 800,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const rowData = row?.original;
          const sections = rowData?.sections?.filter((section) => section);
          return sections?.map((section, index) => (
            <Chip
              key={index}
              label={section?.note}
              style={{
                margin: "5px",
                height: "25px",
                backgroundColor: "#ececec",
              }}
            />
          ));
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        size: 50,
        filterVariant: "select",
        filterFn: booleanFilter,
        filterSelectOptions: [
          { label: "All", value: "" },
          { label: "Active", value: "true" },
          { label: "Inactive", value: "false" },
        ],
        Cell: ({ row }) => {
          const status = row?.original?.status;

          return status ? (
            <Tooltip header={"Enabled"}>
              <CheckCircleIcon style={{ color: "#94BA33" }} />
            </Tooltip>
          ) : (
            <Tooltip header={"Disabled"}>
              <RemoveCircleIcon style={{ color: "red" }} />
            </Tooltip>
          );
        },
      },
      {
        header: "Actions",
        accessorKey: "actions",
        filtering: false,
        size: 10,
        enableSorting: false,
        enableColumnFilter: false,
        accessorFn: (rowData) => {
          return (
            <div style={{ display: "inline-flex" }}>
              <Tooltip header={"Edit"}>
                <EditIcon
                  className="editIcon"
                  style={{ marginRight: "10px" }}
                  onClick={() => {
                    setShowEditModal(true);
                    setIsEditMode(true);
                    setSelectedRow(rowData);
                  }}
                />
              </Tooltip>

              <Tooltip header={"Delete"}>
                <DeleteIcon
                  className="deleteIcon"
                  onClick={() => {
                    setShowConfirmModal(true);
                    setSelectedRow(rowData);
                  }}
                />
              </Tooltip>
            </div>
          );
        },
      },
    ],
    []
  );

  return (
    <>
      {showConfirmModal && (
        <ConfirmModal
          show={showConfirmModal}
          isConfirming={isDeleting}
          onConfirm={onConfirm}
          onCancel={onConfirmModalClose}
          confirmButtonProps={{
            variant: "contained",
            style: { color: "fff", backgroundColor: "#f44336" },
          }}
        />
      )}
      {showEditModal && (
        <ReportNoteTemplateModal
          show={showEditModal}
          isEditMode={isEditMode}
          selectedRow={selectedRow}
          onCloseModal={onCloseModal}
          selectorsData={reportTemplateSelectors}
          fetchReportNoteTemplates={fetchReportNoteTemplates}
        />
      )}

      <CustomTable
        data={reportNoteTemplates ?? []}
        columns={columns}
        options={{
          enableTopToolbar: true,
          enableSorting: true,
          state: { isLoading, showGlobalFilter: true, showColumnFilters: true },
          rowsPerPageOptions: DEFAULT_SMALL_PAGE_SIZE_OPTIONS,
          muiSearchTextFieldProps: {
            placeholder: "Search Templates",
          },
          defaultColumn: {
            size: 250,
          },
          renderTopToolbar: ({ table }) => (
            <Grid
              style={{
                display: "flex",
                gap: "10px",
                padding: "10px 15px",
                borderBottom: "1px solid #e0e0e0",
              }}
            >
              <GlobalFilterTextField table={table} />
              <Button
                onClick={() => {
                  setShowEditModal(true);
                  setIsEditMode(false);
                }}
                variant="contained"
                color="secondary"
                startIcon={<AddIcon />}
              >
                Add Template
              </Button>
            </Grid>
          ),
        }}
      />
    </>
  );
};

export default React.memo(ReportNoteTemplatesView);
